import React, { useState, useRef } from "react";
import { FaShieldAlt, FaUserAlt } from "react-icons/fa";
import { toPng } from "html-to-image";

const DEFAULT_BG =
  "https://images.unsplash.com/photo-1518605368461-1ee7c532066d?q=80&w=1080&auto=format&fit=crop";

const ExportGoalsModal = ({ isOpen, onClose, scorers, subcategory }) => {
  const exportRef = useRef(null);

  const [topLogo, setTopLogo] = useState(
    localStorage.getItem("exportTopLogo") || "",
  );
  const [bottomLogo, setBottomLogo] = useState(
    localStorage.getItem("exportBottomLogo") || "",
  );
  const [bgImage, setBgImage] = useState(
    localStorage.getItem("exportBgImage") || DEFAULT_BG,
  );
  const [isExporting, setIsExporting] = useState(false);

  // ============================================
  // LÓGICA PARA CONSTRUIR EL SUBTÍTULO
  // ============================================
  const getSubtitle = () => {
    let parts = [];

    if (subcategory && typeof subcategory !== "string") {
      // 1. Número de categoría (si existe)
      if (subcategory.number) {
        parts.push(`Categoría ${subcategory.number}`);
      }

      // 2. Nombre del grupo (si existe)
      if (subcategory.group_name) {
        const displayText = subcategory.display || subcategory.name || "";
        if (displayText !== subcategory.group_name) {
          parts.push(subcategory.group_name);
        } else {
          if (!subcategory.number && parts.length === 0) {
            parts.push(subcategory.group_name);
          }
        }
      }

      // 3. Fallback: display o name
      if (parts.length === 0 && subcategory.display) {
        parts.push(subcategory.display);
      } else if (parts.length === 0 && subcategory.name) {
        parts.push(subcategory.name);
      }
    } else if (typeof subcategory === "string" && subcategory) {
      parts.push(subcategory);
    }

    // Fallback desde el primer goleador (si tiene group_name)
    if (parts.length === 0 && scorers.length > 0 && scorers[0].group_name) {
      parts.push(scorers[0].group_name);
    }

    return parts.join(" - ");
  };

  const subtitle = getSubtitle();

  // ============================================
  // MANEJADORES
  // ============================================
  const handleImageUpload = (e, setFunction, storageKey) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1500000) {
        alert(
          "La imagen es muy grande. Por favor sube una imagen menor a 1.5MB",
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFunction(base64String);
        localStorage.setItem(storageKey, base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExport = async () => {
    if (!exportRef.current) return;
    setIsExporting(true);
    try {
      const dataUrl = await toPng(exportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "transparent",
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      const fileName = `Goleadores_${subtitle || "Torneo"}.png`;
      link.download = fileName;
      link.click();
    } catch (error) {
      alert("Hubo un error al generar la imagen.");
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row shadow-2xl">
        {/* Panel de Configuración (Izquierda) */}
        <div className="w-full md:w-1/3 p-6 bg-gray-50 overflow-y-auto border-r border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Exportar Goleadores
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-red-500 font-bold text-xl"
            >
              &times;
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Superior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setTopLogo, "exportTopLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Imagen de Fondo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBgImage, "exportBgImage")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <button
                onClick={() => {
                  setBgImage(DEFAULT_BG);
                  localStorage.removeItem("exportBgImage");
                }}
                className="mt-2 text-xs text-red-500 hover:underline"
              >
                Restaurar fondo por defecto
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Inferior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBottomLogo, "exportBottomLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`w-full py-3 rounded-xl font-bold text-white transition-colors ${
                isExporting ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isExporting ? "Generando..." : "Descargar Imagen"}
            </button>
          </div>
        </div>

        {/* Vista Previa (Derecha) */}
        <div className="w-full md:w-2/3 p-6 bg-gray-200 overflow-y-auto flex items-start justify-center">
          <div
            ref={exportRef}
            className="relative w-full max-w-[500px] bg-blue-900 text-white overflow-hidden shadow-xl"
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              fontFamily: "'Inter', sans-serif",
              padding: "40px 20px",
            }}
          >
            {/* Cabecera */}
            <div className="flex flex-col items-center mb-8">
              {topLogo && (
                <img
                  src={topLogo}
                  alt="Top Logo"
                  className="h-16 object-contain mb-4"
                />
              )}
              <h1
                className="text-5xl font-black tracking-tighter uppercase m-0 leading-none text-center"
                style={{ fontFamily: "Impact, sans-serif" }}
              >
                GOLEADORES
              </h1>
              {subtitle && (
                <h2 className="text-lg font-bold tracking-widest uppercase mt-2 text-blue-200 text-center">
                  {subtitle}
                </h2>
              )}
            </div>

            {/* Lista de Goleadores */}
            <div className="w-full px-2 mb-12 flex flex-col gap-3">
              {scorers.map((player, index) => (
                <div
                  key={player.player_id || index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: "78px",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    padding: "12px 16px",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  {/* Posición */}
                  <div
                    style={{
                      width: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "20px",
                      fontWeight: "900",
                      fontStyle: "italic",
                      color: "rgba(255,255,255,0.8)",
                      marginRight: "12px",
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </div>

                  {/* Foto Jugador */}
                  <div
                    style={{
                      marginRight: "16px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {player.profile_picture_url ? (
                      <img
                        src={player.profile_picture_url}
                        alt="jugador"
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid rgba(255,255,255,0.5)",
                        }}
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(255,255,255,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid rgba(255,255,255,0.5)",
                        }}
                      >
                        <FaUserAlt size={20} />
                      </div>
                    )}
                  </div>

                  {/* Info Jugador & Equipo */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <h3
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        margin: 0,
                      }}
                    >
                      {player.player_name}
                    </h3>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "4px",
                        opacity: 0.8,
                      }}
                    >
                      {player.team_logo ? (
                        <img
                          src={player.team_logo}
                          alt="equipo"
                          style={{
                            width: "16px",
                            height: "16px",
                            marginRight: "8px",
                            objectFit: "contain",
                          }}
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <FaShieldAlt
                          style={{
                            width: "12px",
                            height: "12px",
                            marginRight: "8px",
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: "600",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {player.team_name}
                      </span>
                    </div>
                  </div>

                  {/* Goles */}
                  <div
                    style={{
                      marginLeft: "16px",
                      width: "78px",
                      height: "58px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.15)",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.2)",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: "900",
                        lineHeight: 1,
                      }}
                    >
                      {player.goals}
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: "0.15em",
                        opacity: 0.8,
                        lineHeight: 1,
                        marginTop: "4px",
                      }}
                    >
                      Goles
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pie de página */}
            <div className="mt-auto pt-4 flex justify-center items-center">
              {bottomLogo && (
                <img
                  src={bottomLogo}
                  alt="Bottom Logo"
                  className="h-14 object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportGoalsModal;
